import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import { Flashlight, Keyboard, ScanBarcode } from "lucide-react";
import { toast } from "sonner";
import { hasBarcodeFormat } from "@/lib/barcode";
import { BottomNav } from "@/components/AppShell";
import { useI18n } from "@/i18n/I18nContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function Scan() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const scannerRef = useRef(null);
  const [error, setError] = useState("");
  const [manual, setManual] = useState(false);
  const [code, setCode] = useState("");
  const [torch, setTorch] = useState(false);

  useEffect(() => {
    if (manual) return;

    const regionId = "scan-region";
    const el = document.getElementById(regionId);
    if (el) el.innerHTML = "";

    let scanner = null;
    let cancelled = false;
    let started = false;

    const stop = async () => {
      try {
        if (started && scanner && scanner.isScanning) {
          await scanner.stop();
        }
        if (scanner) scanner.clear();
      } catch (e) {
        try { if (scanner) scanner.clear(); } catch (e2) {}
      }
      const region = document.getElementById(regionId);
      if (region) region.innerHTML = "";
    };

    scanner = new Html5Qrcode(regionId, {
      formatsToSupport: [
        Html5QrcodeSupportedFormats.EAN_13,
        Html5QrcodeSupportedFormats.EAN_8,
        Html5QrcodeSupportedFormats.UPC_A,
        Html5QrcodeSupportedFormats.UPC_E,
        Html5QrcodeSupportedFormats.CODE_128,
        Html5QrcodeSupportedFormats.CODE_39,
        Html5QrcodeSupportedFormats.ITF,
        Html5QrcodeSupportedFormats.CODABAR,
      ],
      experimentalFeatures: { useBarCodeDetectorIfSupported: true },
      verbose: false,
    });
    scannerRef.current = scanner;

    scanner
      .start(
        { facingMode: "environment" },
        {
          fps: 20,
          qrbox: (vw, vh) => {
            const w = Math.floor(Math.min(vw, 360) * 0.85);
            return { width: w, height: Math.floor(w * 0.6) };
          },
          aspectRatio: 1.777,
          videoConstraints: {
            facingMode: "environment",
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
        },
        (decoded) => {
          if (cancelled) return;
          cancelled = true;
          stop().then(() => navigate(`/product/${decoded}`));
        },
        () => {}
      )
      .then(() => {
        started = true;
        if (cancelled) stop();
      })
      .catch(() => {
        if (!cancelled) setError(t("scan.noCamera"));
      });

    return () => {
      cancelled = true;
      stop();
    };
  }, [manual, navigate]);

  const toggleTorch = async () => {
    try {
      const next = !torch;
      await scannerRef.current?.applyVideoConstraints({ advanced: [{ torch: next }] });
      setTorch(next);
    } catch {
      toast.info(t("scan.torchUnsupported"));
    }
  };

  const submitManual = () => {
    if (!hasBarcodeFormat(code)) {
      toast.error(t("scan.invalidBarcode"));
      return;
    }
    navigate(`/product/${code.trim()}`);
  };

  return (
    <div className="flex flex-col h-[100dvh] bg-slate-900">
      <div className="flex-1 relative overflow-hidden">
        {!manual && <div id="scan-region" className="absolute inset-0 w-full h-full object-cover [&_video]:object-cover [&_video]:w-full [&_video]:h-full" />}

        {!manual && (
          <div className="absolute inset-0 pointer-events-none flex flex-col items-center justify-center">
            {/* затемнённая маска с прозрачным окном */}
            <div className="relative w-[280px] h-[190px]">
              <div className="absolute inset-0 rounded-3xl shadow-[0_0_0_2000px_rgba(15,23,42,0.55)]" />
              {/* угловые скобки */}
              <span className="absolute -top-0.5 -left-0.5 w-8 h-8 border-t-4 border-l-4 border-emerald-400 rounded-tl-2xl" />
              <span className="absolute -top-0.5 -right-0.5 w-8 h-8 border-t-4 border-r-4 border-emerald-400 rounded-tr-2xl" />
              <span className="absolute -bottom-0.5 -left-0.5 w-8 h-8 border-b-4 border-l-4 border-emerald-400 rounded-bl-2xl" />
              <span className="absolute -bottom-0.5 -right-0.5 w-8 h-8 border-b-4 border-r-4 border-emerald-400 rounded-br-2xl" />
              {/* анимированная линия сканирования */}
              <div className="absolute inset-x-2 top-0 h-full overflow-hidden rounded-2xl">
                <div className="scan-line absolute inset-x-0 top-0 h-0.5 bg-emerald-400 shadow-[0_0_12px_2px_rgba(52,211,153,0.9)]" />
              </div>
            </div>
            <p className="mt-6 text-sm font-medium text-white/90 px-8 text-center" data-testid="scan-hint">{t("scan.hint")}</p>
          </div>
        )}

        {error && (
          <div className="absolute inset-x-6 top-6 text-center text-sm text-white bg-red-500/90 rounded-xl py-2 px-3" data-testid="scan-error">
            {error}
          </div>
        )}

        {!manual && (
          <button
            onClick={toggleTorch}
            data-testid="torch-button"
            className={`absolute bottom-5 right-5 w-12 h-12 rounded-full flex items-center justify-center backdrop-blur-md transition-colors ${torch ? "bg-yellow-400 text-slate-900" : "bg-white/15 text-white"}`}
          >
            <Flashlight className="w-5 h-5" />
          </button>
        )}
      </div>

      {manual ? (
        <div className="px-4 pt-4 pb-28 bg-[#F4F0EA] rounded-t-3xl -mt-4 relative z-10">
          <p className="font-heading font-bold text-slate-800 mb-3">{t("scan.enterBarcode")}</p>
          <div className="flex gap-2">
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder={t("scan.enterBarcode")}
              inputMode="numeric"
              className="rounded-xl"
              data-testid="manual-barcode-input"
            />
            <Button onClick={submitManual} data-testid="manual-search-button" className="rounded-xl bg-primary shrink-0">
              <ScanBarcode className="mr-2 h-4 w-4" />
              {t("scan.go")}
            </Button>
          </div>
          <button onClick={() => setManual(false)} data-testid="back-to-camera-button" className="mt-3 text-sm font-semibold text-emerald-600">
            {t("scan.backToCamera")}
          </button>
        </div>
      ) : (
        <div className="px-4 pt-4 pb-28 bg-[#F4F0EA] rounded-t-3xl -mt-4 relative z-10">
          <Button
            variant="outline"
            className="w-full h-12 rounded-xl border-slate-200 text-slate-700 font-semibold"
            onClick={() => setManual(true)}
            data-testid="manual-toggle-button"
          >
            <Keyboard className="mr-2 h-4 w-4" />
            {t("scan.enterManually")}
          </Button>
        </div>
      )}

      <BottomNav />
    </div>
  );
}
